import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import LeaveConflictsPanel from "./LeaveConflictsPanel";
import { REFETCH_LEAVES, UPDATE_STATUS, WAIVE_LEAVE_DOCUMENT, assertSuccess } from "./leaveShared";

// Review a pending request: details, document, projected fine, staffing
// and booking conflicts, the stored capacity snapshot, then decide.

const RISK_BADGE = {
  LOW: "bg-green-100 text-green-800",
  MEDIUM: "bg-yellow-100 text-yellow-800",
  HIGH: "bg-orange-100 text-orange-800",
};

const Caption = ({ children }) => (
  <span className="text-xs font-semibold uppercase text-gray-400">{children}</span>
);

const LeaveApprovalModal = ({ leave, onClose }) => {
  const [remark, setRemark] = useState(leave.adminRemark || "");
  const [saving, setSaving] = useState(false);
  const [updateStatus] = useMutation(UPDATE_STATUS, { refetchQueries: REFETCH_LEAVES });
  const [waiveDocument] = useMutation(WAIVE_LEAVE_DOCUMENT, { refetchQueries: REFETCH_LEAVES });
  const [documentWaived, setDocumentWaived] = useState(false);
  const markDocumentNotNeeded = async () => {
    if (!window.confirm("Mark the document as not needed for this leave?")) return;
    try {
      const out = (await waiveDocument({ variables: { leaveId: leave.id } })).data.waiveLeaveDocument;
      if (out.status !== "SUCCESS") throw new Error(out.message);
      setDocumentWaived(true);
    } catch (err) {
      alert(err.message);
    }
  };
  const snapshot = leave.impactSnapshot;

  const decide = async (status) => {
    setSaving(true);
    try {
      const res = await updateStatus({
        variables: { data: { leaveId: leave.id, status, adminRemark: remark } },
      });
      assertSuccess(res.data?.updateLeaveStatus);
      onClose();
    } catch (err) {
      alert("Error updating leave status: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg p-6 w-[560px] shadow-xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-xl font-bold mb-4 text-gray-800 border-b pb-2">Review Leave Request</h2>

        <div className="space-y-3 mb-6">
          <div>
            <Caption>Employee</Caption>
            <p className="text-sm font-medium text-gray-800">{leave.employee?.fullName}</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <Caption>Duration</Caption>
              <p className="text-sm text-gray-700">
                {leave.startDate} to {leave.endDate}
              </p>
            </div>
            <div>
              <Caption>Mode & Type</Caption>
              <p className="text-sm text-gray-700">
                {leave.leaveMode} ({leave.leaveType})
              </p>
            </div>
          </div>
          <div>
            <Caption>Reason</Caption>
            <p className="text-sm text-gray-700 italic bg-gray-50 p-2 rounded border">{leave.reason}</p>
          </div>
          {(leave.hasDocument || (leave.documentRequired && !documentWaived)) && (
            <div>
              <Caption>Document</Caption>
              {leave.hasDocument ? (
                <a
                  href={leave.documentUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-sm text-blue-600 underline"
                >
                  View attached document
                </a>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm text-amber-700">
                    Required for this leave but not attached yet - the employee can still add it.
                  </p>
                  <button
                    type="button"
                    onClick={markDocumentNotNeeded}
                    className="text-xs font-semibold text-blue-600 hover:text-blue-800 whitespace-nowrap"
                  >
                    Not needed
                  </button>
                </div>
              )}
            </div>
          )}
          {leave.projectedPenaltyAmount != null && (
            <div className="mt-2 bg-orange-50 border border-orange-200 p-2 rounded">
              <span className="text-xs font-semibold uppercase text-orange-800">Projected Base Penalty</span>
              <p className="text-sm font-bold text-orange-600">₹{leave.projectedPenaltyAmount}</p>
              <p className="text-[10px] text-orange-500 mt-0.5">
                Note: If marked as 'Absent', the higher unapproved rates apply instead.
              </p>
            </div>
          )}
        </div>

        <LeaveConflictsPanel leaveId={leave.id} />

        <div className="mb-6 p-4 rounded-lg bg-gray-50 border border-gray-200">
          <h3 className="text-sm font-bold text-gray-700 mb-3 flex items-center justify-between">
            <span>Capacity Impact Analysis</span>
            {snapshot && (
              <span
                className={`px-2 py-0.5 rounded text-xs font-bold ${
                  RISK_BADGE[snapshot.riskLevel] || "bg-red-100 text-red-800"
                }`}
              >
                {snapshot.riskLevel} RISK
              </span>
            )}
          </h3>
          {snapshot ? (
            <div className="grid grid-cols-2 gap-4 text-xs">
              <div>
                <span className="text-gray-500">Active Staff Available:</span>
                <p className="font-semibold text-gray-800">{snapshot.activeStaffCount}</p>
              </div>
              <div>
                <span className="text-gray-500">Expected Bookings:</span>
                <p className="font-semibold text-gray-800">{snapshot.expectedBookingsCount}</p>
              </div>
              <div>
                <span className="text-gray-500">Capacity Deficit:</span>
                <p className="font-semibold text-red-600">{snapshot.capacityDeficit} hrs</p>
              </div>
              <div>
                <span className="text-gray-500">Required Hours:</span>
                <p className="font-semibold text-gray-800">{snapshot.totalServiceHoursRequired} hrs</p>
              </div>
            </div>
          ) : (
            <p className="text-xs text-gray-500 italic">
              No capacity analysis details stored for this leave request.
            </p>
          )}
        </div>

        <div className="mb-6">
          <label className="block text-xs font-semibold uppercase text-gray-400 mb-1">Admin Remark</label>
          <textarea
            className="w-full border p-2 rounded text-sm bg-white"
            placeholder="Enter remarks..."
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
          />
        </div>

        <div className="flex justify-between items-center">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded text-sm font-medium hover:bg-gray-100 transition"
          >
            Close
          </button>
          <div className="flex space-x-2">
            {[
              ["ABSENT", "Mark Absent", "bg-gray-800 hover:bg-gray-900"],
              ["REJECTED", "Reject", "bg-red-600 hover:bg-red-700"],
              ["APPROVED", "Approve", "bg-green-600 hover:bg-green-700"],
            ].map(([status, label, color]) => (
              <button
                key={status}
                type="button"
                disabled={saving}
                onClick={() => decide(status)}
                className={`px-4 py-2 ${color} text-white rounded text-sm font-medium transition disabled:opacity-50`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeaveApprovalModal;
